// src/components/Sidebar.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../components/Images/KostPadi-Icon.png";
import "./sidebar.css";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import {
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Users,
  FileText,
  Receipt,
  Settings,
  BarChart3,
  Layers,
  CreditCard,
  Wallet,
  FolderKanban,
  Plus,
  BookOpen,
  Layers3,
  Warehouse,
  ListOrdered,
  SlidersHorizontal,
  PackageSearch,
  BookOpenCheck,
  Calendar,
  BarChart2,
  WalletCards,
  Calendar1,
} from "lucide-react";

const PLAN_RANK = {
  free: 0,
  starter: 1,
  sme: 2,
  business: 3,
};

function normalizePlan(p) {
  const v = String(p || "free")
    .toLowerCase()
    .trim();

  if (v === "basic") return "starter";
  if (v === "premium") return "sme";
  if (v === "pro") return "sme";
  if (["free", "starter", "sme", "business"].includes(v)) return v;

  return "free";
}

function normalizeRole(role) {
  return String(role || "")
    .toLowerCase()
    .trim();
}

function pathMatches(pathname, allowedPath) {
  return pathname === allowedPath || pathname.startsWith(`${allowedPath}/`);
}

const ROLE_ALLOWED_PATHS = {
  operations: [
    "/dashboard",
    "/customers",
    "/sales-quick",
    "/purchases-quick",
    "/scheduling",
    "/bookings",
    "/budgets",
    "/vendors",
    "/inventory",
    "/inventory/items",
    "/inventory/movements",
    "/inventory/stock-on-hand",
    "/fixed-assets",
    "/subledger/ar",
    "/subledger/ap",
    "/estimate",
    "/invoice",
    "/receipt",
    "/total_estimate",
    "/total_invoice",
    "/totalReceipts",
    "/income",
    "/sales-history",
    "/purchase-history",
    "/account-ledger",
    "/inventory-valuation",
  ],

  reviewer: [
    "/dashboard",
    "/inventory",
    "/report",
    "/income",
    "/sofp",
    "/cashFlows",
    "/managementReport",
    "/packs",
    "/inventory-valuation",
    "/stock-ageing",
  ],
};

function canAccessPath({ isBusinessPlan, companyRole, businessRole, to }) {
  if (!to) return true;

  const legacyRole = normalizeRole(companyRole);
  const role = normalizeRole(businessRole);

  if (!isBusinessPlan) {
    return true;
  }

  if (role === "superadmin") return true;

  if (role === "manager") {
    return !["/team-settings", "/billings", "/billing/callback"].some((p) =>
      pathMatches(to, p)
    );
  }

  if (role === "accountant") {
    return ![
      "/settings",
      "/team-settings",
      "/billings",
      "/billing/callback",
    ].some((p) => pathMatches(to, p));
  }

  if (role === "operations") {
    if (pathMatches(to, "/journal") || pathMatches(to, "/total_journals")) {
      return false;
    }

    return ROLE_ALLOWED_PATHS.operations.some((p) => pathMatches(to, p));
  }

  if (role === "reviewer") {
    return ROLE_ALLOWED_PATHS.reviewer.some((p) => pathMatches(to, p));
  }

  if (role === "hr") return pathMatches(to, "/payroll");

  if (role === "marketing") return pathMatches(to, "/crm");

  return legacyRole === "admin";
}

export default function Sidebar({ setIsLoggedIn }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { company, companyId, companyRole, businessRole, plan } = useCompany();

  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showReportMenu, setShowReportMenu] = useState(false);
  const [showInventoryMenu, setShowInventoryMenu] = useState(false);
  const [showTaxMenu, setShowTaxMenu] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    supabase.auth.getUser();
  }, []);

  const normalizedPlan = useMemo(() => normalizePlan(plan), [plan]);
  const isBusinessPlan = normalizedPlan === "business";

  const planLabel =
    normalizedPlan.charAt(0).toUpperCase() + normalizedPlan.slice(1);

  const currentBusinessRole = normalizeRole(businessRole);

  const isSuperAdmin = isBusinessPlan
    ? currentBusinessRole === "superadmin"
    : companyRole === "admin";

  const canSee = useCallback(
    (minPlan = "free") => {
      const need = normalizePlan(minPlan);
      return (PLAN_RANK[normalizedPlan] ?? 0) >= (PLAN_RANK[need] ?? 0);
    },
    [normalizedPlan]
  );

  const canSeeRoute = useCallback(
    (to) =>
      canAccessPath({
        isBusinessPlan,
        companyRole,
        businessRole,
        to,
      }),
    [isBusinessPlan, companyRole, businessRole]
  );

  useEffect(() => {
    if (!canSee("starter")) setShowCreateMenu(false);
    if (!canSee("sme")) setShowReportMenu(false);
    if (!canSee("sme")) setShowInventoryMenu(false);
    if (!canSee("business")) setShowTaxMenu(false);
  }, [canSee]);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();

    if (!error) {
      localStorage.removeItem("isLoggedIn");
      setIsLoggedIn(false);
      navigate("/login");
    }
  };

  const isActive = (path) => location.pathname.startsWith(path);

  const MenuItem = ({ to, icon: Icon, label }) => {
    const active = isActive(to);

    return (
      <li className={`kp-item ${active ? "active" : ""}`}>
        <Link
          to={to}
          className="kp-link"
          data-tip={collapsed ? label : undefined}
        >
          <Icon className="kp-ico" size={18} />
          {!collapsed && <span className="label">{label}</span>}
          {active && (
            <span className="kp-active-indicator" aria-hidden="true" />
          )}
        </Link>
      </li>
    );
  };

  const menu = useMemo(() => {
    const items = [];

    items.push({
      type: "item",
      to: "/dashboard",
      icon: LayoutDashboard,
      label: "Dashboard",
      min: "free",
    });

    if (isSuperAdmin || (isBusinessPlan && currentBusinessRole === "manager")) {
      items.push({
        type: "item",
        to: "/settings",
        icon: Settings,
        label: "Setup",
        min: "free",
      });
    }

    if (isSuperAdmin) {
      items.push({
        type: "item",
        to: "/billings",
        icon: CreditCard,
        label: "Billings",
        min: "free",
      });
    }

    if (
      isSuperAdmin ||
      (isBusinessPlan &&
        ["manager", "accountant"].includes(currentBusinessRole))
    ) {
      items.push(
        {
          type: "item",
          to: "/chart-of-accounts",
          icon: Wallet,
          label: "Account Chart",
          min: "business",
        },
        {
          type: "item",
          to: "/total_journals",
          icon: BookOpenCheck,
          label: "Journals",
          min: "sme",
        },
        {
          type: "item",
          to: "/BankTransactions",
          icon: BookOpenCheck,
          label: "Bank Transactions",
          min: "business",
        },
        {
          type: "item",
          to: "/Reconcilliation",
          icon: Wallet,
          label: "Reconciliation",
          min: "business",
        }
      );
    }

    items.push(
      {
        type: "item",
        to: "/customers",
        icon: Users,
        label: "Customers",
        min: "free",
      },
      {
        type: "item",
        to: "/sales-quick",
        icon: Receipt,
        label: "Quick Sales",
        min: "starter",
      },
      {
        type: "item",
        to: "/vendors",
        icon: Users,
        label: "Vendors",
        min: "sme",
      },
      {
        type: "item",
        to: "/purchases-quick",
        icon: FolderKanban,
        label: "Quick Purchases",
        min: "sme",
      },
      {
        type: "item",
        to: "/scheduling",
        icon: Calendar,
        label: "Bookings",
        min: "business",
      },
      {
        type: "item",
        to: "/budgets",
        icon: BarChart2,
        label: "Budgeting",
        min: "business",
      },
      {
        type: "item",
        to: "/payroll",
        icon: WalletCards,
        label: "Payroll Management",
        min: "business",
      },
      {
        type: "item",
        to: "/crm",
        icon: Calendar1,
        label: "CRM Management",
        min: "business",
      }
    );

    items.push({
      type: "group",
      key: "create",
      label: "Create",
      icon: Plus,
      min: "free",
      open: showCreateMenu,
      onToggle: () => setShowCreateMenu((v) => !v),
      children: [
        { to: "/estimate", icon: Layers, label: "Estimate", min: "starter" },
        { to: "/invoice", icon: FileText, label: "Invoice", min: "free" },
        { to: "/receipt", icon: Receipt, label: "Receipt", min: "starter" },
        {
          to: "/journal",
          icon: FolderKanban,
          label: "Journal",
          min: "business",
        },
      ],
    });

    items.push({
      type: "group",
      key: "inventory",
      label: "Inventory",
      icon: Layers3,
      min: "sme",
      open: showInventoryMenu,
      onToggle: () => setShowInventoryMenu((v) => !v),
      children: [
        {
          to: "/inventory",
          icon: LayoutDashboard,
          label: "Dashboard",
          min: "sme",
        },
        {
          to: "/inventory/items",
          icon: PackageSearch,
          label: "Items",
          min: "sme",
        },
        {
          to: "/inventory/warehouses",
          icon: Warehouse,
          label: "Warehouses",
          min: "business",
        },
        {
          to: "/inventory/movements",
          icon: ListOrdered,
          label: "Movements",
          min: "sme",
        },
        {
          to: "/inventory/stock-on-hand",
          icon: Layers,
          label: "Stock On Hand",
          min: "sme",
        },
        {
          to: "/inventory/settings",
          icon: SlidersHorizontal,
          label: "Settings",
          min: "business",
        },
      ],
    });

    items.push({
      type: "group",
      key: "tax",
      label: "Tax Management",
      icon: Layers3,
      min: "business",
      open: showTaxMenu,
      onToggle: () => setShowTaxMenu((v) => !v),
      children: [
        {
          to: "/tax",
          icon: LayoutDashboard,
          label: "Tax Dashboard",
          min: "business",
        },
        {
          to: "/tax/configuration",
          icon: PackageSearch,
          label: "Tax Configuration",
          min: "business",
        },
        {
          to: "/tax/periods",
          icon: Warehouse,
          label: "Tax Periods",
          min: "business",
        },
        { to: "/tax/vat", icon: ListOrdered, label: "VAT", min: "business" },
        { to: "/tax/wht", icon: Layers, label: "WHT", min: "business" },
        {
          to: "/tax/cit",
          icon: SlidersHorizontal,
          label: "CIT",
          min: "business",
        },
        {
          to: "/tax/development-levy",
          icon: SlidersHorizontal,
          label: "Development Levy",
          min: "business",
        },
        {
          to: "/tax/filings",
          icon: SlidersHorizontal,
          label: "Tax Filings",
          min: "business",
        },
      ],
    });

    items.push({
      type: "group",
      key: "reports",
      label: "Reports",
      icon: BarChart3,
      min: "sme",
      open: showReportMenu,
      onToggle: () => setShowReportMenu((v) => !v),
      children: [
        {
          to: "/income",
          icon: BarChart3,
          label: "Income (Profit Report)",
          min: "sme",
        },
        { to: "/cashFlows", icon: Wallet, label: "Cashflow", min: "sme" },
        {
          to: "/sales-history",
          icon: FileText,
          label: "Sales History",
          min: "sme",
        },
        {
          to: "/purchase-history",
          icon: FileText,
          label: "Purchase History",
          min: "sme",
        },
        {
          to: "/sofp",
          icon: BarChart3,
          label: "Balance Sheet",
          min: "business",
        },
        {
          to: "/account-ledger",
          icon: BookOpen,
          label: "Account Ledger",
          min: "business",
        },
        {
          to: "/GeneralLedger",
          icon: BarChart3,
          label: "General Ledger",
          min: "business",
        },
        {
          to: "/TrialBalance",
          icon: BarChart3,
          label: "Trial Balance",
          min: "business",
        },
        {
          to: "/managementReport",
          icon: BarChart3,
          label: "Management Report",
          min: "business",
        },
        {
          to: "/packs",
          icon: BarChart3,
          label: "Financial Statement",
          min: "business",
        },
        {
          to: "/inventory-valuation",
          icon: BarChart3,
          label: "Inventory Valuation",
          min: "business",
        },
        {
          to: "/stock-ageing",
          icon: BarChart3,
          label: "Stock Ageing",
          min: "business",
        },
      ],
    });

    items.push(
      {
        type: "item",
        to: "/fixed-assets",
        icon: Receipt,
        label: "Fixed Assets",
        min: "business",
      },
      {
        type: "item",
        to: "/subledger/ar",
        icon: Receipt,
        label: "Account Receivables",
        min: "business",
      },
      {
        type: "item",
        to: "/subledger/ap",
        icon: Receipt,
        label: "Account Payables",
        min: "business",
      },
      {
        type: "item",
        to: "/total_estimate",
        icon: Layers,
        label: "All Estimates",
        min: "starter",
      },
      {
        type: "item",
        to: "/total_invoice",
        icon: FileText,
        label: "All Invoices",
        min: "starter",
      },
      {
        type: "item",
        to: "/totalReceipts",
        icon: Receipt,
        label: "All Receipts",
        min: "starter",
      }
    );

    if (isSuperAdmin) {
      items.push({
        type: "item",
        to: "/team-settings",
        icon: Users,
        label: "Team Settings",
        min: "business",
      });
    }

    return items;
  }, [
    isSuperAdmin,
    isBusinessPlan,
    currentBusinessRole,
    showCreateMenu,
    showInventoryMenu,
    showTaxMenu,
    showReportMenu,
  ]);

  const visibleMenu = useMemo(() => {
    return menu
      .filter((it) => canSee(it.min || "free"))
      .filter((it) => !it.to || canSeeRoute(it.to))
      .map((it) => {
        if (it.type !== "group") return it;

        const children = (it.children || [])
          .filter((c) => canSee(c.min || it.min || "free"))
          .filter((c) => canSeeRoute(c.to));

        if (!children.length) return null;

        return { ...it, children };
      })
      .filter(Boolean);
  }, [menu, canSee, canSeeRoute]);

  return (
    <motion.nav
      className={`sidebar-container ${collapsed ? "collapsed" : ""}`}
      initial={{ x: -250 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="sidebar glass">
        <div className="sidebar-header">
          <img src={logo} alt="Logo" className="logo" />

          {!collapsed && (
            <div className="company-wrap">
              <h4 className="company-name" title={company?.company_name || ""}>
                {company?.company_company_name ||
                  company?.company_name ||
                  "KostPadi"}
              </h4>

              {companyId && (
                <span className="company-id">
                  ID: {String(companyId).slice(0, 8)}…
                </span>
              )}

              <span className={`plan-pill plan-${normalizedPlan}`}>
                {planLabel} plan
              </span>

              {isBusinessPlan && currentBusinessRole && (
                <span className="company-id">
                  Role:{" "}
                  {currentBusinessRole.charAt(0).toUpperCase() +
                    currentBusinessRole.slice(1)}
                </span>
              )}
            </div>
          )}
        </div>

        <ul
          id="sidebar-menu"
          className="kp-menu"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
            gap: 0,
          }}
        >
          {visibleMenu.map((it) => {
            if (it.type === "item") {
              return (
                <MenuItem
                  key={it.to}
                  to={it.to}
                  icon={it.icon}
                  label={it.label}
                />
              );
            }

            const open = Boolean(it.open);
            const Icon = it.icon;

            return (
              <li
                key={it.key}
                className={`kp-item has-children ${open ? "open" : ""}`}
              >
                <button
                  className="kp-link btnlike"
                  onClick={it.onToggle}
                  data-tip={collapsed ? it.label : undefined}
                  aria-expanded={open}
                  aria-controls={`${it.key}-submenu`}
                >
                  <Icon className="kp-ico" size={18} />
                  {!collapsed && <span className="label">{it.label}</span>}
                  {!collapsed && <ChevronRight className="chev" size={16} />}
                </button>

                <AnimatePresence initial={false}>
                  {open && !collapsed && (
                    <motion.ul
                      id={`${it.key}-submenu`}
                      className="kp-submenu"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {(it.children || []).map((c) => (
                        <li key={c.to}>
                          <Link to={c.to} className="kp-sublink">
                            {c.icon ? <c.icon size={16} /> : null}
                            <span className="label">{c.label}</span>
                          </Link>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
        </ul>

        <div className="sidebar-footer">
          <button
            className="logout-btn"
            onClick={handleLogout}
            data-tip={collapsed ? "Logout" : undefined}
          >
            {!collapsed ? <span className="label">Logout</span> : "🔒"}
          </button>
        </div>

        <button
          className="collapse-btn"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>
    </motion.nav>
  );
}
